import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { C } from '@ridgewill/brand';

/**
 * The modal system the portal did not have.
 *
 * Everything consequential was an inline `<details>` disclosure. That is not
 * strictly worse — no focus trap to get wrong, no portal, and it is deep-linkable
 * — but it cannot do "click your avatar to open settings", and it puts a
 * consequential control below a table somebody is scrolling.
 *
 * ── WHAT A MODAL IS FOR HERE, since 2026-08-28 ───────────────────────
 *
 * CONFIRMATIONS AND PICKERS, and nothing else. Publish a version, retire an
 * application, add members, issue a credential: things you say yes or no to and
 * then leave. Record DETAIL VIEWS were dialogs and are screens now — see
 * components/Record.tsx for what that cost, in one sentence: the record was not
 * a location, so a link to it opened or apologised depending on what the reader
 * had searched for.
 *
 * `ModalTabs` went with them. A modal is not a location, so a tab inside one
 * could never write the hash; tabs that ARE addresses live in `TabNav`.
 *
 * ── Portalled, because of stacking contexts ──────────────────────────
 *
 * Rendered into `<body>` rather than in place. An overlay inside a table cell
 * inherits every `overflow`, `transform` and `position` between it and the root,
 * and the failure is a dialog clipped to a table cell — which looks like a
 * layout bug and is actually a rendering-tree one.
 *
 * ── The three ways out, and why all three ────────────────────────────
 *
 * Escape, the close control, and clicking the backdrop. People reach for
 * different ones and a dialog that only closes by its × is one that traps
 * somebody who reached for Escape first. The backdrop close is bound to
 * mousedown ON THE BACKDROP ITSELF — a click that starts inside the card and
 * ends outside it (selecting text, dragging past the edge) must not close, and
 * binding to `click` is how it does.
 */
export function Modal({
  title,
  subtitle,
  onClose,
  footer,
  width = 560,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  width?: number;
  children: ReactNode;
}) {
  const card = useRef<HTMLDivElement>(null);
  const backdropArmed = useRef(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Stopped, so one Escape closes one layer. A detail modal opened from a
        // list must not also dismiss whatever is behind it.
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    /**
     * The content behind does not scroll while this is open.
     *
     * Without it, a wheel over the backdrop scrolls the list underneath, so
     * closing the dialog leaves the admin somewhere they did not navigate to —
     * and on a long list they cannot find the row they just opened.
     *
     * `[data-scroll-root]`, NOT `body`. org-admin#7 moved the scrolling off the
     * page and into the main frame so a scrollbar could not resize the header —
     * which silently made a lock on `body` a no-op, because `body` is no longer
     * what scrolls. Falling back to `body` keeps this honest if the layout ever
     * moves back.
     */
    const target = document.querySelector<HTMLElement>('[data-scroll-root]') ?? document.body;
    /**
     * `overflowY`, NOT the `overflow` shorthand.
     *
     * The scroll container sets `overflow-y: auto` as an inline style. Writing
     * the shorthand overrides it, and restoring the shorthand to its previous
     * value — the empty string, because nothing ever set it — clears BOTH axes,
     * taking `overflow-y: auto` with it. React then does not put it back,
     * because its virtual DOM never changed.
     *
     * Measured: closing a modal left the list unscrollable at `visible`. The
     * dialog worked, the lock worked, and the page silently lost its scrollbar
     * on the way out.
     */
    const previous = target.style.overflowY;
    target.style.overflowY = 'hidden';
    return () => {
      target.style.overflowY = previous;
    };
  }, []);

  useEffect(() => {
    /**
     * Focus moves INTO the dialog, to the first control rather than to the card.
     *
     * A dialog that opens without moving focus leaves the keyboard behind it:
     * Tab continues through the list underneath, which is both invisible and
     * still actionable. Falling back to the card itself (it has `tabIndex={-1}`)
     * keeps a dialog with no controls reachable by Escape.
     */
    const first = card.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (first ?? card.current)?.focus();
  }, []);

  const onBackdropDown = useCallback((e: React.MouseEvent) => {
    // Armed only if the press STARTED on the backdrop. See the header.
    backdropArmed.current = e.target === e.currentTarget;
  }, []);

  const onBackdropUp = useCallback(
    (e: React.MouseEvent) => {
      if (backdropArmed.current && e.target === e.currentTarget) onClose();
      backdropArmed.current = false;
    },
    [onClose],
  );

  return createPortal(
    <div
      style={s.overlay}
      onMouseDown={onBackdropDown}
      onMouseUp={onBackdropUp}
      /* Not `role="dialog"` on the backdrop — the card carries it, so the
         labelled region is the thing with the content in it. */
    >
      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{ ...s.card, maxWidth: width }}
        className="rw-modal"
      >
        <header style={s.head}>
          <div style={{ minWidth: 0 }}>
            <h2 style={s.title}>{title}</h2>
            {subtitle ? <div style={s.subtitle}>{subtitle}</div> : null}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={s.close} className="rw-press">
            ✕
          </button>
        </header>

        <div style={s.body}>{children}</div>

        {footer ? <footer style={s.foot}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

/** The footer pair. Cancel first in the DOM so it is the first thing Tab
 *  reaches, and on the left, because the destructive-adjacent one should not be
 *  where the pointer lands by default. */
export function ModalActions({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'flex-end' }}>{children}</div>;
}

const s = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    // A scrim's job is to separate the modal from the page behind it. Tuned
    // for a light page it is too weak over a near-black one; see styles.css.
    background: 'var(--rw-admin-scrim, rgba(16, 22, 38, .38))',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '4rem 1rem 2rem',
    overflowY: 'auto' as const,
    zIndex: 40,
  },
  card: {
    background: C.surface,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    boxShadow: 'var(--rw-admin-shadow, 0 12px 40px rgba(16, 22, 38, .18))',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: 'calc(100vh - 6rem)',
    outline: 'none',
    /**
     * Trims the square children to the rounded frame (#13).
     *
     * The card was already `borderRadius: 12` and the bottom corners still came
     * out square, because `foot` paints an opaque `C.page` background right
     * over them — the radius was there, the footer was drawn on top of it. The
     * head does not show the problem only because the body below it is
     * transparent.
     *
     * `Panel` in ui.tsx solves the identical thing the identical way, which is
     * why this is `overflow` rather than a matching radius on the footer: a
     * radius has to be repeated on whichever child happens to be last, and gets
     * forgotten the first time that changes.
     *
     * Safe now in a way it would not have been last week: the menus that used
     * to need to escape a modal are portalled to the body (#11), so nothing
     * inside relies on overflowing the card. The body keeps its own
     * `overflowY: auto`, so long content still scrolls rather than being cut.
     */
    overflow: 'hidden' as const,
  },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '1.1rem 1.3rem .9rem',
    borderBottom: `1px solid ${C.border}`,
  },
  title: { margin: 0, fontSize: '1rem', fontWeight: 680, letterSpacing: '-.01em' },
  subtitle: { marginTop: '.2rem', fontSize: '.82rem', color: C.muted, lineHeight: 1.45 },
  close: {
    background: 'none',
    border: 'none',
    fontSize: '.95rem',
    color: C.muted,
    cursor: 'pointer',
    padding: '.2rem .35rem',
    borderRadius: 6,
    lineHeight: 1,
    flex: 'none',
  },
  // The body scrolls, not the card — so the header and the footer stay put and
  // the Submit control does not walk off the bottom of a long form.
  body: { padding: '1.1rem 1.3rem', overflowY: 'auto' as const, display: 'grid', gap: '1rem' },
  foot: { padding: '.9rem 1.3rem', borderTop: `1px solid ${C.border}`, background: C.page },
};
